import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const NavigationContext = createContext();

export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [navigationStack, setNavigationStack] = useState([]);

  // Track navigation history
  useEffect(() => {
    setNavigationStack(prev => {
      // Don't add duplicate consecutive entries
      if (prev.length > 0 && prev[prev.length - 1].pathname === location.pathname) {
        return prev;
      }
      return [...prev, { pathname: location.pathname, search: location.search }];
    });
  }, [location.pathname, location.search]);

  // Determine if back button should be shown
  const canGoBack = useCallback(() => {
    const currentPath = location.pathname;
    const searchParams = new URLSearchParams(location.search);

    // Rating mode in PackageView
    if (searchParams.get('mode') === 'rating') {
      return true;
    }

    // Don't show back button on home/dashboard UNLESS it's a login flow
    if (currentPath === '/' || currentPath === '/trade-packages/dashboard') {
      // Hide back button during login flow - user should complete login
      if (searchParams.get('login') === 'true') {
        return false;
      }
      return false;
    }

    // Pages where back button should show
    const backButtonPages = [
      '/orphan-marketplace', // Orphan marketplace detail pages
      '/startup-marketplace', // Startup marketplace detail pages
      '/marketplaces', // Main marketplace page
      '/intel', // Manager Intel landing and profile pages
      '/trade-packages/leagues',
      '/trade-packages/league',
      '/trade-packages/builder',
      '/manager-intel',
      '/trade-packages/marketplace',
      '/trade-packages/canvas',
      '/trade-packages/', // PackageView with unique_link
    ];

    return backButtonPages.some(page => currentPath.startsWith(page));
  }, [location.pathname, location.search]);

  // Get the appropriate back destination
  const getBackDestination = useCallback(() => {
    const currentPath = location.pathname;
    const searchParams = new URLSearchParams(location.search);

    // From PackageView rating mode → overview mode
    if (searchParams.get('mode') === 'rating') {
      return currentPath; // Stay on same page but remove mode param
    }

    // From PackageView (unique_link) → Dashboard
    // PackageView URLs are like /trade-packages/cf2a1fc92000613...
    // They don't have other path segments like /leagues, /league, /builder, /canvas, /marketplace, /orphan-marketplace
    if (currentPath.startsWith('/trade-packages/') &&
        !currentPath.includes('/leagues') &&
        !currentPath.includes('/league/') &&
        !currentPath.includes('/builder') &&
        !currentPath.includes('/canvas') &&
        !currentPath.includes('/marketplace') &&
        !currentPath.includes('/orphan-marketplace') &&
        currentPath !== '/trade-packages/dashboard') {
      return '/trade-packages/dashboard';
    }

    // From Manager Intel profile page → wherever they came from
    if (currentPath.startsWith('/manager-intel/')) {
      // Check navigation stack for previous route
      const previousRoute = navigationStack[navigationStack.length - 2];
      if (previousRoute && previousRoute.pathname !== currentPath) {
        return previousRoute.pathname + (previousRoute.search || '');
      }
      // Fallback to intel landing if no history
      return '/intel';
    }

    // From Manager Intel landing → Home (/)
    if (currentPath === '/intel') {
      return '/';
    }

    // From TradeCanvas (canvas with recipient) → Discovery Feed
    // New route: /canvas/:username/:league_id/:recipient_id
    if (currentPath.match(/\/trade-packages\/canvas\/[^/]+\/[^/]+\/[^/]+/)) {
      const match = currentPath.match(/\/canvas\/([^/]+)\/([^/]+)\//);
      if (match) {
        const username = match[1];
        const leagueId = match[2];
        return `/trade-packages/marketplace/${username}/${leagueId}`;
      }
    }

    // From Marketplace → LeaguesList (back to the leagues picker)
    if (currentPath.match(/\/trade-packages\/marketplace\/[^/]+\/[^/]+/)) {
      const match = currentPath.match(/\/marketplace\/([^/]+)\//);
      if (match) {
        const username = match[1];
        return `/trade-packages/leagues/${username}`;
      }
    }

    // From LeaguesList → Dashboard
    if (currentPath.startsWith('/trade-packages/leagues/')) {
      return '/trade-packages/dashboard';
    }

    // From LeagueDetail → LeaguesList
    if (currentPath.startsWith('/trade-packages/league/')) {
      const username = sessionStorage.getItem('tradePackages_creatorUsername');
      if (username) {
        return `/trade-packages/leagues/${username}`;
      }

      // Try navigation stack as fallback
      const previousRoute = navigationStack[navigationStack.length - 2];
      if (previousRoute && previousRoute.pathname.startsWith('/trade-packages/leagues/')) {
        return previousRoute.pathname;
      }

      return '/trade-packages/dashboard';
    }

    // Orphan Marketplace navigation
    // From /orphan-marketplace/:id/apply → /orphan-marketplace/:id
    if (currentPath.match(/\/orphan-marketplace\/[^/]+\/apply/)) {
      const match = currentPath.match(/\/orphan-marketplace\/([^/]+)\/apply/);
      if (match) {
        const id = match[1];
        return `/orphan-marketplace/${id}`;
      }
    }

    // From /orphan-marketplace/:id/applications → /orphan-marketplace/:id
    if (currentPath.match(/\/orphan-marketplace\/[^/]+\/applications/)) {
      const match = currentPath.match(/\/orphan-marketplace\/([^/]+)\/applications/);
      if (match) {
        const id = match[1];
        return `/orphan-marketplace/${id}`;
      }
    }

    // From /orphan-marketplace/list → check where user came from
    if (currentPath === '/orphan-marketplace/list') {
      // Check navigation stack to see where they came from
      const previousRoute = navigationStack[navigationStack.length - 2];
      if (previousRoute) {
        // If they came from homepage, go back to homepage
        if (previousRoute.pathname === '/' || previousRoute.pathname === '/trade-packages/dashboard') {
          return previousRoute.pathname;
        }
        // If they came from marketplace, go back there
        if (previousRoute.pathname === '/marketplaces' || previousRoute.pathname === '/orphan-marketplace') {
          return '/marketplaces';
        }
      }
      // Default to marketplaces
      return '/marketplaces';
    }

    // From /startup-marketplace/list → /marketplaces
    if (currentPath === '/startup-marketplace/list') {
      return '/marketplaces?type=startup';
    }

    // From /dashboard → /marketplaces
    if (currentPath === '/dashboard') {
      return '/marketplaces';
    }

    // From /orphan-marketplace/:id → /marketplaces
    if (currentPath.match(/^\/orphan-marketplace\/[^/]+$/)) {
      return '/marketplaces';
    }

    // From /startup-marketplace/:id → /marketplaces
    if (currentPath.match(/^\/startup-marketplace\/[^/]+$/)) {
      return '/marketplaces?type=startup';
    }

    // From /marketplaces → Home (/)
    if (currentPath === '/marketplaces' || currentPath === '/orphan-marketplace') {
      return '/';
    }

    // Default: go to home
    return '/';
  }, [location.pathname, location.search, location.state, navigationStack]);

  // Smart back navigation
  const goBack = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);

    // Special handling for rating mode: remove mode param
    if (searchParams.get('mode') === 'rating') {
      searchParams.delete('mode');
      const newUrl = `${location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
      navigate(newUrl);
      return;
    }

    // Clean up Manager Intel back URL from sessionStorage when leaving
    if (location.pathname.startsWith('/manager-intel/') || location.pathname.startsWith('/intel')) {
      sessionStorage.removeItem('managerIntel_backUrl');
    }

    const destination = getBackDestination();
    navigate(destination);
  }, [getBackDestination, navigate, location.pathname, location.search]);

  // Custom navigate that tracks navigation
  const customNavigate = useCallback((to, options = {}) => {
    navigate(to, options);
  }, [navigate]);

  // Check if current page is Dashboard
  const isDashboard = useCallback(() => {
    return location.pathname === '/' || location.pathname === '/trade-packages/dashboard';
  }, [location.pathname]);

  // Check if we're in PackageView rating mode
  const isRatingMode = useCallback(() => {
    return location.pathname.startsWith('/trade-packages/') &&
           location.pathname.length > '/trade-packages/'.length &&
           !location.pathname.includes('/leagues') &&
           !location.pathname.includes('/league/') &&
           !location.pathname.includes('/builder') &&
           !location.pathname.includes('/dashboard');
  }, [location.pathname]);

  const value = {
    canGoBack,
    goBack,
    navigate: customNavigate,
    getBackDestination,
    isDashboard,
    isRatingMode,
    currentPath: location.pathname,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
